import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import {
  LayoutDashboard,
  Building2,
  Bell,
  LogOut,
  Receipt,
  Wrench,
  Star,
  Users as UsersIcon,
  Settings as SettingsIcon,
  HelpCircle,
  BarChart3,
  MessageSquare,
  Menu,
  X,
  SlidersHorizontal, Pin, PanelLeft, ClipboardList,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useFileUrl } from "../lib/files";
import { canUsePushNotifications, enablePushNotifications } from "../lib/push";
import { NAV_STAGES, STAGE_LABELS, STAGE_COLORS } from "../lib/stages";
import { canAccessPage, canAccessStage, hasAnyPermission, hasLution } from "../lib/permissions";
import ApplicationSelectors from "../components/ApplicationSelectors";
import { useApplication } from "../context/ApplicationContext";
import {newActivityInstance,recordActivity} from "../lib/activity";

export const sidebarPreferenceKey = userId => `webstax.sidebar.pinned.${userId || "anonymous"}`;
export const readSidebarPinned = userId => typeof window === "undefined" || localStorage.getItem(sidebarPreferenceKey(userId)) !== "false";
export const desktopSidebarExpanded = (pinned, hovered) => Boolean(pinned || hovered);
export const workflowNavigationStages = (workflow, user) => (workflow?.stages || [])
  .filter(stage => stage.enabled && stage.navigation_visible)
  .filter(stage => canAccessStage(user, stage.key));

export default function Layout() {
  const { user, logout, refreshMe } = useAuth();
  const { settings } = useSettings();
  const { effectiveWorkflow } = useApplication();
  const nav = useNavigate();
  const location = useLocation();
  useEffect(()=>{if(!user?.id)return;const path=location.pathname,module=path.startsWith("/config")?"configlution":path.startsWith("/facilities")?"facilities":"tar";recordActivity({event_type:"page.viewed",module,page_key:path,page_label:document.title},newActivityInstance())},[location.key,location.pathname,user?.id]);
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const preferenceKey = sidebarPreferenceKey(user?.id);
  const [sidebarPinned, setSidebarPinned] = useState(() => readSidebarPinned(user?.id));
  const [sidebarHover, setSidebarHover] = useState(false);
  const sidebarExpanded = desktopSidebarExpanded(sidebarPinned, sidebarHover);
  const setPinned = (next) => { setSidebarPinned(next); localStorage.setItem(preferenceKey, String(next)); };
  useEffect(() => {
    setSidebarPinned(localStorage.getItem(preferenceKey) !== "false");
  }, [preferenceKey]);
  const [lastDesktopNotifId, setLastDesktopNotifId] = useState(null);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showNotifEnablePrompt, setShowNotifEnablePrompt] = useState(false);
  const [showIosInstallHint, setShowIosInstallHint] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "" });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const logoUrl = useFileUrl(settings?.logo_path);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        setNotifs(data);
      } catch (err) {
        if (err?.response?.status && err.response.status !== 401) {
          console.error("Layout notifications poll failed:", err);
        }
      }
    };
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ua = window.navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua);
    const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
    const dismissed = localStorage.getItem("ios_install_hint_dismissed") === "1";
    if (isIos && !isStandalone && !dismissed) setShowIosInstallHint(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    const syncPrompt = () => {
      if (Notification.permission === "granted") {
        localStorage.setItem("notif_prompt_dismissed", "1");
        setShowNotifEnablePrompt(false);
        return;
      }
      const dismissed = localStorage.getItem("notif_prompt_dismissed") === "1";
      setShowNotifEnablePrompt(!dismissed && Notification.permission === "default");
    };
    syncPrompt();
    window.addEventListener("focus", syncPrompt);
    document.addEventListener("visibilitychange", syncPrompt);
    let permissionStatus;
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "notifications" }).then((status) => {
        permissionStatus = status;
        permissionStatus.onchange = syncPrompt;
      }).catch(() => {});
    }
    return () => {
      window.removeEventListener("focus", syncPrompt);
      document.removeEventListener("visibilitychange", syncPrompt);
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, []);

  const unread = (Array.isArray(notifs) ? notifs : []).filter((n) => !n.read).length;
  const unreadMessages = (Array.isArray(notifs) ? notifs : []).filter((n) => !n.read && n.stage === "query").length;
  const canSeeStatistics = canAccessPage(user, "statistics");
  // `role` is a legacy presentation field. Managed Organisation Administrators
  // are identified by their catalogue-backed authority, not that old label.
  const isAdmin = (user?.permission_keys || []).includes("config.roles.delete");
  const isStaff = user?.role === "staff";
  const canSeeCleaningAdminUsers = user?.role === "cleaning_admin";
  const canSeeConfig = hasLution(user, "config") && hasAnyPermission(user, ["config.locations.read", "config.portfolios.read", "config.sites.read", "config.roles.read", "config.users.read", "config.imports.read", "config.imports.run", "config.branding.read", "config.workflows.read", "config.shared_settings.read", "config.room_types.read", "config.rules.read", "config.flags.read", "config.key_return.read", "config.bulk_actions.read"]);
  const canSeeTar = hasLution(user, "tar") || !(user?.lution_entitlements || []).length;
  const canSeeFacilities = (hasLution(user, "facilities") || !(user?.lution_entitlements || []).length) && canAccessPage(user, "repairs");

  const configuredStages = useMemo(() => effectiveWorkflow?.stages?.filter(stage => stage.enabled && stage.navigation_visible) ||
    NAV_STAGES.map(key => ({key, display_name: STAGE_LABELS[key], colour: STAGE_COLORS[key]?.text})), [effectiveWorkflow]);
  const allowedStages = effectiveWorkflow ? workflowNavigationStages(effectiveWorkflow, user) : configuredStages.filter(stage => canAccessStage(user, stage.key));

  const markRead = async () => {
    await api.post("/notifications/read-all");
    const { data } = await api.get("/notifications");
    setNotifs(data);
  };

  const notificationPath = useCallback((n) => {
    if (n.stage === "query") return "/messages";
    if (n.stage && configuredStages.some(stage => stage.key === n.stage) && n.room_id) return `/stage/${n.stage}?room=${n.room_id}`;
    return "/";
  }, [configuredStages]);

  const openNotification = (n) => {
    setShowNotif(false);
    nav(notificationPath(n));
  };

  useEffect(() => {
    const latest = (notifs || [])[0];
    if (!latest || latest.read) return;
    if (latest.id === lastDesktopNotifId) return;
    setLastDesktopNotifId(latest.id);
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      const notice = new Notification("Turnaround notification", { body: latest.text || "New update", data: { url: notificationPath(latest) } });
      notice.onclick = () => {
        window.focus();
        nav(notificationPath(latest));
      };
    }
  }, [notifs, lastDesktopNotifId, nav, notificationPath]);

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
    };
    const onInstalled = () => setInstallPromptEvent(null);
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const submitRequiredPasswordChange = async () => {
    if (passwordForm.next.length < 10) {
      toast.error("New password must be at least 10 characters");
      return;
    }
    setPasswordSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: passwordForm.current, new_password: passwordForm.next });
      setPasswordForm({ current: "", next: "" });
      await refreshMe();
      toast.success("Password changed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setPasswordSaving(false);
    }
  };

  const platformName = settings?.platform_name || "TURNAROUND";
  const brandPrimary = settings?.brand_primary || "#0A0A0A";
  const brandText = settings?.brand_text || "#FFFFFF";

  const Sidebar = (mobile = false) => {
    if (!mobile && !sidebarExpanded) return (
      <aside className="h-full w-16 border-r border-slate-200 bg-white shadow-sm flex flex-col items-center" data-testid="desktop-icon-rail" onMouseEnter={() => setSidebarHover(true)}>
        <Link to="/" aria-label={`${platformName} home`} title={`${platformName} home`} className="my-3 flex h-10 w-10 items-center justify-center rounded-xl" style={{background:brandPrimary,color:brandText}}>{platformName[0]}</Link>
        <button type="button" onClick={() => setPinned(true)} aria-label="Pin navigation" title="Pin navigation" data-testid="sidebar-pin-toggle" className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100"><PanelLeft size={18}/></button>
        <nav aria-label="Collapsed navigation" className="flex min-h-0 flex-1 flex-col items-center gap-1 overflow-y-auto py-1">
          {canSeeTar && <><RailItem to="/" end label="Dashboard" icon={LayoutDashboard}/>{canAccessPage(user,"blocks")&&<RailItem to="/blocks" label="Blocks" icon={Building2}/>} {canAccessPage(user,"special_considerations")&&<RailItem to="/special-considerations" label="Special Considerations" icon={Star}/>} {canAccessPage(user,"charges")&&<RailItem to="/charges" label="Charges" icon={Receipt}/>} {canSeeStatistics&&<RailItem to="/statistics" label="Statistics" icon={BarChart3}/>} {allowedStages.map(stage=><RailStage key={stage.key} stage={stage.key} label={stage.display_name}/>)}</>}
          {canSeeFacilities&&<RailItem to="/repairs" label="Repairs & maintenance" icon={Wrench}/>}<RailItem to="/messages" label="Communications" icon={MessageSquare}/>
          {canSeeConfig && <RailItem to="/config" label="Administration" icon={SlidersHorizontal}/>}
        </nav>
        <div className="flex flex-col gap-1 border-t border-slate-200 py-2"><RailItem to="/help" label="Help" icon={HelpCircle}/><RailItem to="/settings" label="Settings" icon={SettingsIcon}/><button type="button" aria-label="Log out" title="Log out" data-testid="logout-button" onClick={async()=>{await logout();nav("/login");}} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100"><LogOut size={17}/></button></div>
      </aside>
    );
    return (
    <aside
      className={`${mobile ? "w-full" : (sidebarExpanded ? "w-64" : "w-16")} border-r transition-[width] duration-200 ${!mobile && !sidebarExpanded ? "sidebar-collapsed" : ""} border-gray-200 bg-white h-full flex flex-col`}
      data-testid="app-sidebar"
      onMouseEnter={() => !mobile && !sidebarPinned && setSidebarHover(true)}
      onMouseLeave={() => !mobile && setSidebarHover(false)}
    >
      <Link to="/" onClick={() => setDrawer(false)} className="flex items-center gap-2 p-5 border-b border-gray-200" data-testid="organisation-identity">
        {logoUrl ? (
          <img src={logoUrl} alt={`${platformName} organisation logo`} className="w-8 h-8 object-contain" />
        ) : (
          <div
            className="w-8 h-8 flex items-center justify-center font-chivo font-black"
            style={{ background: brandPrimary, color: brandText }}
          >
            {platformName[0]}
          </div>
        )}
        <div>
          <div className="font-chivo font-black text-base leading-none">WEBSTAX</div>
          <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
            Shared application shell
          </div>
          <div className="mt-1 max-w-36 truncate text-xs font-semibold" style={{ color: "var(--brand-accent)" }}>{platformName}</div>
        </div>
      </Link>

      {!mobile && <button type="button" onClick={() => setPinned(!sidebarPinned)} className="mx-3 mt-3 flex items-center gap-3 p-2 text-xs font-bold uppercase" aria-label={sidebarPinned ? "Unpin navigation" : "Pin navigation"} title={sidebarPinned ? "Unpin navigation" : "Pin navigation"} data-testid="sidebar-pin-toggle">{sidebarPinned ? <Pin size={16}/> : <PanelLeft size={16}/>}<span>{sidebarPinned ? "Unpin" : "Pin open"}</span></button>}
      <nav className="flex-1 overflow-y-auto py-3" onClick={() => setDrawer(false)}>
        {canSeeTar && <><SectionLabel>TARlution</SectionLabel>
        <NavItem to="/" end label="Dashboard" icon={LayoutDashboard} />
        {canAccessPage(user, "blocks") && <NavItem to="/blocks" label="Blocks" icon={Building2} />}
        {canAccessPage(user, "special_considerations") && <NavItem to="/special-considerations" label="Special Considerations" icon={Star} />}
        {canAccessPage(user, "charges") && <NavItem to="/charges" label="Charges" icon={Receipt} />}
        {canSeeStatistics && <NavItem to="/statistics" label="Statistics" icon={BarChart3} />}
        {canSeeCleaningAdminUsers && (
          <NavItem to="/cleaning-admin" label="Cleaners" icon={UsersIcon} />
        )}

        {allowedStages.length > 0 && <SectionLabel>Workflow stages</SectionLabel>}
        {allowedStages.map((stage) => {
          const s = stage.key;
          const c = STAGE_COLORS[s] || {text: stage.colour || "#64748b"};
          return (
            <NavLink
              key={s}
              to={`/stage/${s}`}
              data-testid={`nav-stage-${s}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-5 py-2 text-sm font-medium border-l-2 transition-colors ${
                  isActive
                    ? "brand-nav-active bg-gray-50 text-black"
                    : "border-transparent text-gray-700 hover:bg-gray-50"
                }`
              }
            >
              <span className="w-2 h-2" style={{ backgroundColor: c.text }} />
              {stage.display_name}
            </NavLink>
          );
        })}</>}
        {canSeeFacilities && <><SectionLabel>Facilution</SectionLabel><NavItem to="/repairs" label="Repairs & maintenance" icon={Wrench} /></>}
        <SectionLabel>Shared services</SectionLabel>
        <NavItem to="/messages" label="Communications" icon={MessageSquare} badge={unreadMessages > 0 ? unreadMessages : null} />
        {canSeeConfig && <><SectionLabel>Configlution</SectionLabel><NavItem to="/config" label="Administration" icon={SlidersHorizontal} />{(user?.permission_keys||[]).includes("audit.read")&&<NavItem to="/config/audit" label="Audit Log" icon={ClipboardList} />}</>}
      </nav>

      <div className="border-t border-gray-200 p-4">
        <div className="text-xs font-mono text-gray-500 uppercase mb-1">Signed in</div>
        <div className="text-sm font-semibold truncate" data-testid="current-user-email">
          {user?.email}
        </div>
        <div className="text-xs text-gray-500 uppercase tracking-wider font-mono mb-3">
          {user?.role}
        </div>
        <Link
          to="/help"
          onClick={() => setDrawer(false)}
          className="w-full flex items-center justify-center gap-2 bg-white text-black border border-gray-300 hover:border-gray-600 px-3 py-2 text-xs font-semibold uppercase tracking-wider mb-2"
        >
          <HelpCircle size={14} /> Help
        </Link>
        <Link
          to="/settings"
          onClick={() => setDrawer(false)}
          className="w-full flex items-center justify-center gap-2 bg-white text-black border border-gray-300 hover:border-gray-600 px-3 py-2 text-xs font-semibold uppercase tracking-wider mb-2"
        >
          <SettingsIcon size={14} /> Settings
        </Link>
        <button
          onClick={async () => {
            setDrawer(false);
            await logout();
            nav("/login");
          }}
          data-testid="logout-button"
          className="w-full flex items-center justify-center gap-2 bg-white text-black border border-gray-300 hover:border-gray-600 px-3 py-2 text-xs font-semibold uppercase tracking-wider"
        >
          <LogOut size={14} /> Log out
        </button>
      </div>
    </aside>
    );
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      {/* Desktop/landscape sidebar */}
      <div className={`app-desktop-sidebar fixed left-0 top-0 h-screen z-30 transition-[width] duration-200 ${sidebarExpanded ? "w-64" : "w-16"}`}>
        {Sidebar(false)}
      </div>

      {/* Mobile/tablet portrait drawer */}
      {drawer && (
        <div className="app-mobile-only fixed inset-0 z-50">
          <div className="w-full h-full bg-white relative">
            <button
              onClick={() => setDrawer(false)}
              className="absolute top-3 right-3 p-2 border border-gray-300 bg-white z-10"
              data-testid="mobile-menu-close"
            >
              <X size={18} />
            </button>
            {Sidebar(true)}
          </div>
        </div>
      )}

      <header
        className={`${sidebarPinned ? "app-desktop-offset" : "app-desktop-offset-collapsed"} app-topbar h-14 border-b border-gray-200 bg-white flex min-w-0 items-center px-2 sm:px-4 sticky top-0 z-20 gap-1.5 sm:gap-3`}
        data-testid="app-topbar"
      >
        <button
          className="app-mobile-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-gray-100"
          onClick={() => setDrawer(true)}
          data-testid="menu-button"
        >
          <Menu size={20} />
        </button>
        <Link to="/" className="app-mobile-flex min-w-0 shrink items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt={`${platformName} organisation logo`} className="w-7 h-7 object-contain" />
          ) : (
            <div
              className="w-7 h-7 flex items-center justify-center font-chivo font-black text-xs"
              style={{ background: brandPrimary, color: brandText }}
            >
              {platformName[0]}
            </div>
          )}
          <div className="mobile-brand-name font-chivo font-black text-sm">WEBSTAX</div>
        </Link>
        <div className="min-w-0 flex-1"></div>
        <ApplicationSelectors />
        <div className="relative">
          <button
            onClick={() => {
              setShowNotif((s) => !s);
              if (!showNotif && unread) markRead();
            }}
            data-testid="notification-bell"
            className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-gray-100"
          >
            <Bell size={18} />
            {unread > 0 && (
              <span className="absolute top-1 right-1 bg-red-600 text-white text-[9px] font-bold px-1 min-w-[14px] text-center">
                {unread}
              </span>
            )}
          </button>
          {showNotif && (
            <div className="absolute right-0 top-12 w-80 max-w-[calc(100vw-1rem)] sm:w-96 sm:max-w-[28rem] bg-white border border-gray-300 shadow-md max-h-96 overflow-auto z-30" data-testid="notifications-panel">
              <div className="px-3 py-2 border-b border-gray-200 font-chivo font-bold text-xs uppercase tracking-wider">
                Activity
              </div>
              {notifs.length === 0 && (
                <div className="p-4 text-xs text-gray-500">No activity yet.</div>
              )}
              {notifs.map((n) => (
                <button key={n.id} onClick={() => openNotification(n)} className="w-full text-left px-3 py-2 border-b border-gray-100 text-xs hover:bg-gray-50">
                  <div>{n.text}</div>
                  <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                    {new Date(n.at).toLocaleString()}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {user?.password_change_required && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" data-testid="required-password-change-modal">
          <div className="bg-white border border-gray-300 w-full max-w-md">
            <div className="px-5 py-3 border-b border-gray-200">
              <div className="font-chivo font-black text-lg">Change password required</div>
              <div className="text-xs text-gray-600 mt-1">You must change your password before continuing.</div>
            </div>
            <div className="p-5 space-y-3">
              <label className="block text-xs font-mono uppercase tracking-wider text-gray-500">
                Current password
                <input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })} className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" data-testid="required-current-password" />
              </label>
              <label className="block text-xs font-mono uppercase tracking-wider text-gray-500">
                New password
                <input type="password" value={passwordForm.next} onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })} className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" data-testid="required-new-password" />
              </label>
              <div className="text-[11px] text-gray-500">Minimum 10 characters with uppercase, lowercase and a number.</div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
              <button onClick={submitRequiredPasswordChange} disabled={passwordSaving} className="bg-black text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-50" data-testid="required-password-submit">
                {passwordSaving ? "Updating…" : "Change password"}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className={`${sidebarPinned ? "app-desktop-offset" : "app-desktop-offset-collapsed"} app-main-content p-4 min-h-screen flex flex-col`}>
        {installPromptEvent && (
          <div className="mb-4 border border-blue-200 bg-blue-50 p-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs sm:text-sm text-blue-900">
              Install Turnaround HQ for faster access and notifications on your device.
            </div>
            <button
              onClick={async () => {
                installPromptEvent.prompt();
                await installPromptEvent.userChoice;
                setInstallPromptEvent(null);
              }}
              className="shrink-0 px-3 py-1.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wider border border-blue-700 text-blue-800 bg-white"
            >
              Add to home screen
            </button>
          </div>
        )}
        {showIosInstallHint && !installPromptEvent && (
          <div className="mb-4 border border-blue-200 bg-blue-50 p-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs sm:text-sm text-blue-900">
              Add this app to your Home Screen from Safari Share menu for quicker access.
            </div>
            <button
              onClick={() => {
                localStorage.setItem("ios_install_hint_dismissed", "1");
                setShowIosInstallHint(false);
              }}
              className="shrink-0 px-3 py-1.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wider border border-blue-300 bg-white"
            >
              Got it
            </button>
          </div>
        )}
        {showNotifEnablePrompt && (
          <div className="mb-4 border border-amber-200 bg-amber-50 p-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs sm:text-sm text-amber-900">
              Enable push notifications to receive stage and query alerts, even when the app is closed where supported.
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  if (!canUsePushNotifications()) {
                    localStorage.setItem("notif_prompt_dismissed", "1");
                    setShowNotifEnablePrompt(false);
                    return;
                  }
                  try {
                    await enablePushNotifications();
                    if (!("Notification" in window) || Notification.permission === "granted") {
                      localStorage.setItem("notif_prompt_dismissed", "1");
                      setShowNotifEnablePrompt(false);
                    }
                  } catch (err) {
                    console.error("Push notification enable failed:", err);
                  }
                }}
                className="shrink-0 px-3 py-1.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wider border border-amber-700 text-amber-900 bg-white"
              >
                Turn on
              </button>
              <button
                onClick={() => {
                  localStorage.setItem("notif_prompt_dismissed", "1");
                  setShowNotifEnablePrompt(false);
                }}
                className="shrink-0 px-3 py-1.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wider border border-amber-300"
              >
                Not now
              </button>
            </div>
          </div>
        )}
        <div className="flex-1">
          <Outlet key={user?.active_site_id || "all-sites"} />
        </div>

        <div className="text-center text-[10px] font-mono text-gray-400 pt-6 mt-8 border-t border-gray-200">
          © 2026 Webstax Lutions LTD. All rights reserved.
        </div>
      </main>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="px-5 pt-4 pb-1 text-[10px] font-mono uppercase tracking-[0.18em] text-gray-400">
      {children}
    </div>
  );
}

function NavItem({ to, end, label, icon: Icon, adminPill, badge }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-5 py-2 text-sm font-medium border-l-2 transition-colors ${
          isActive
            ? "brand-nav-active bg-gray-50 text-black"
            : "border-transparent text-gray-700 hover:bg-gray-50"
        }`
      }
    >
      <Icon size={15} />
      <span className="flex-1">{label}</span>
      {badge ? (
        <span className="text-[10px] font-mono font-bold bg-red-600 text-white px-1.5 min-w-[18px] text-center">
          {badge}
        </span>
      ) : null}
      {adminPill && (
        <span className="text-[9px] font-mono uppercase tracking-wider border border-gray-300 px-1 text-gray-500">
          Admin
        </span>
      )}
    </NavLink>
  );
}

export function RailItem({ to, end, label, icon: Icon }) {
  return <NavLink to={to} end={end} aria-label={label} title={label} data-testid={`rail-${label.toLowerCase().replace(/\s+/g,"-")}`} className={({isActive})=>`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-l-2 transition ${isActive?"brand-nav-active bg-indigo-50 text-indigo-800 shadow-sm":"border-transparent text-slate-600 hover:bg-slate-100"}`}><Icon aria-hidden="true" size={18}/><span className="sr-only">{label}</span></NavLink>;
}

function RailStage({ stage, label }) {
  const colour=STAGE_COLORS[stage]?.text||"#64748b";
  const displayLabel=label||STAGE_LABELS[stage]||"Workflow stage";
  return <NavLink to={`/stage/${stage}`} aria-label={displayLabel} title={displayLabel} data-testid={`rail-stage-${stage}`} className={({isActive})=>`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-l-2 ${isActive?"brand-nav-active bg-slate-100 shadow-sm":"border-transparent hover:bg-slate-50"}`}><span aria-hidden="true" className="h-3 w-3 rounded-full ring-2 ring-white shadow" style={{backgroundColor:colour}}/><span className="sr-only">{displayLabel}</span></NavLink>;
}
